import React from "react";
import { Modal, Button, Table } from "react-bootstrap";

const ViewApprovalRequestModal = ({ show, handleClose, approvalRequest }) => {
  // Return null or a placeholder if no request data is available yet
  if (!approvalRequest) return null;

  const { approvable_type, new_data, original_data } = approvalRequest;
  
  // Parse the JSON strings into objects, handling null cases
  const parsedOriginalData = original_data ? 
    (typeof original_data === 'string' ? JSON.parse(original_data) : original_data) : 
    null;
  const parsedNewData = new_data ? 
    (typeof new_data === 'string' ? JSON.parse(new_data) : new_data) : 
    null;
  
  const isUpdate = original_data !== null; // Determine if it's an update request

  // Helper function to get a user-friendly resource type name
  const getResourceType = (type) => {
    switch (type) {
      case "App\\Models\\Project":
        return "Project";
      case "App\\Models\\Unit":
        return "Unit";
      case "App\\Models\\Developer":
        return "Developer";
      // Add more cases as needed for other models
      default:
        // Attempt to extract the model name if not explicitly defined
        const modelName = type?.split('\\').pop();
        return modelName || "Item"; // Fallback to 'Item' or the extracted name
    }
  };

  const resourceType = getResourceType(approvable_type);

  // Mapping for backend field keys to user-friendly labels
  const friendlyLabels = {
    // General
    name: "Name",
    description: "Description",
    address: "Address",
    location: "Location", // Could be an object or string
    status: "Status",
    type: "Type",
    notes: "Notes",
    // Contact/Bank
    contactNumber: "Contact Number",
    email: "Email",
    iban: "IBAN",
    swiftCode: "SWIFT Code",
    nameOfAccount: "Account Holder Name",
    bankName: "Bank Name",
    branchNumber: "Branch Number",
    bankAddress: "Bank Address",
    // Segel
    segelName: "Segel Name",
    segelNumber: "Segel Number",
    // Project Specific
    startingPrice: "Starting Price",
    deliveryDate: "Delivery Date",
    isLaunch: "Launch Status",
    isSponsored: "Sponsored Status",
    incentive: "Incentive",
    incentiveStartDate: "Incentive Start Date",
    incentiveEndDate: "Incentive End Date",
    projectCommission: "Project Commission",
    // Unit Specific
    price: "Price",
    area: "Area (sqm)",
    numberOfBathrooms: "Bathrooms",
    numberOfBedrooms: "Bedrooms",
    finishing: "Finishing",
    floorNumber: "Floor Number",
    // Common Meta
    updated_at: "Last Updated",
    created_at: "Created On",
    updatePending: "Update Pending",
    // Image/Gallery Keys (if they have specific names)
    mainImage: "Main Image",
    logo: "Logo",
    coverImage: "Cover Image",
    gallery: "Gallery",
    floorPlan: "Floor Plan",
    // Add any other specific field keys from your models
  };

  // Helper function to format timestamps (adjust format as needed)
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '-'; // Handle null or undefined timestamps
    try {
      const date = new Date(timestamp);
      // Check if the date object is valid
      if (isNaN(date.getTime())) {
        // Attempt to parse differently if needed, or return placeholder
        console.warn("Invalid date format received:", timestamp);
        return '-';
      }

      const day = date.getDate().toString().padStart(2, "0");
      const month = (date.getMonth() + 1).toString().padStart(2, "0"); // Month is 0-indexed
      const year = date.getFullYear();
      const hours = date.getHours().toString().padStart(2, "0");
      const minutes = date.getMinutes().toString().padStart(2, "0");
      return `${day}-${month}-${year} ${hours}:${minutes}`; // Format: DD-MM-YYYY HH:MM
    } catch (error) {
      console.error("Error formatting timestamp:", timestamp, error);
      return '-'; // Return placeholder on error
    }
  };


  // Function to process data: separate fields, images, galleries, format values
  const processFields = (data) => {
    if (!data || typeof data !== 'object') {
      return { fields: [], images: [], galleries: [] };
    }

    const fields = [];
    const images = [];
    const galleries = [];

    Object.entries(data).forEach(([key, value]) => {
      // --- Skip IDs (allow specific ones like 'user_id' if needed) ---
      if (key.toLowerCase().endsWith('id') && key.toLowerCase() !== 'user_id' /* add exceptions */) {
        return;
      }

      let displayValue = value; // Value used for display

      // --- Format Timestamps ---
      // Be more specific: Check key names AND if value looks like a timestamp string
      if ((key.toLowerCase().includes('date') || key.endsWith('_at')) && typeof value === 'string' && value.length > 10) { // Basic check
        // Further check if it's likely a parseable date
        if (!isNaN(Date.parse(value))) {
          displayValue = formatTimestamp(value);
        }
      }


      // --- Classify as Image, Gallery, or Field ---
      const isString = typeof value === 'string';
      const looksLikeUrl = isString && (value.startsWith('http://') || value.startsWith('https://'));
      const looksLikeBase64 = isString && value.startsWith('data:image');
      const isImageArray = Array.isArray(value) && value.length > 0 && value.every(
        item => typeof item === 'string' && (item.startsWith('http') || item.startsWith('data:image'))
      );

      // First check if it's a gallery
      if (isImageArray) {
        galleries.push({ key, value });
      } 
      // Then check if it's a single image
      else if (looksLikeUrl || looksLikeBase64) {
        // Check if the key suggests it's an image (more comprehensive check)
        const isImageKey = key.toLowerCase().includes('image') || 
                          key.toLowerCase().includes('logo') || 
                          key.toLowerCase().includes('cover') || 
                          key.toLowerCase().includes('photo') || 
                          key.toLowerCase().includes('picture') ||
                          key.toLowerCase().includes('gallery') ||
                          key.toLowerCase().includes('masterplan')

        if (isImageKey || (!key.toLowerCase().includes('url') && (looksLikeBase64 || value.match(/\.(jpg|jpeg|png|gif|webp)$/i)))) {
          images.push({ key, value });
        } else {
          fields.push({ key, value: displayValue });
        }
      } else {
        // --- Handle Other Data Types for Display ---
        if (typeof value === 'boolean') {
          displayValue = value ? "Yes" : "No";
        } else if (value === null || typeof value === 'undefined') {
          displayValue = "-"; // Placeholder for null/undefined
        } else if (typeof value === 'object') {
          // Convert non-null objects/arrays (that aren't galleries) to string for display
          try {
            displayValue = JSON.stringify(value, null, 2); // Pretty print JSON
          } catch (e) {
            displayValue = "[Complex Data]"; // Fallback
          }
        }
        // Add the processed field
        fields.push({ key, value: displayValue });
      }
    });

    // Sort fields alphabetically by key for consistent order (optional)
    // fields.sort((a, b) => a.key.localeCompare(b.key));

    return { fields, images, galleries };
  };

  // Process both new and original data with parsed objects
  const newDataProcessed = processFields(parsedNewData);
  const originalDataProcessed = processFields(parsedOriginalData);

  // Helper function to auto-format keys if no friendly label exists
  const formatKey = (key) => {
    return friendlyLabels[key] || key
      .replace(/([A-Z])/g, ' $1') // Add space before capital letters
      .replace(/_/g, ' ') // Replace underscores with spaces
      .replace(/^./, str => str.toUpperCase()); // Capitalize first letter
  }

  // Helper to render media comparison (images/galleries)
  const renderMediaComparison = (key, oldMedia, newMedia) => {
    const isGallery = oldMedia?.type === 'gallery' || newMedia?.type === 'gallery';
    const oldValue = oldMedia?.value;
    const newValue = newMedia?.value;

    return (
      <div className="d-flex justify-content-around">
        {/* Original Media */}
        <div className="w-50 px-1 text-center">
          <small>Original</small>
          <div className="d-flex flex-wrap justify-content-center mt-1">
            {isGallery ? (
              (oldValue || []).length > 0 ? (oldValue || []).map((img, idx) => (
                <a 
                  key={`orig-${key}-${idx}`} 
                  href={img} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="m-1"
                >
                  <img 
                    src={img} 
                    alt={`Original ${key}-${idx}`} 
                    style={{ 
                      height: '50px', 
                      width: '50px', 
                      objectFit: 'cover', 
                      cursor: 'pointer' 
                    }} 
                    className="img-thumbnail" 
                    title="Click to view full size"
                  />
                </a>
              )) : '-'
            ) : (
              oldValue ? (
                <a 
                  href={oldValue} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="d-block"
                >
                  <img 
                    src={oldValue} 
                    alt={`Original ${key}`} 
                    style={{ 
                      maxHeight: '70px', 
                      maxWidth: '100%', 
                      objectFit: 'contain',
                      cursor: 'pointer'
                    }} 
                    className="img-thumbnail"
                    title="Click to view full size"
                  />
                </a>
              ) : '-'
            )}
          </div>
        </div>
        {/* Updated Media */}
        <div className="w-50 px-1 text-center">
          <small>Updated</small>
          <div className="d-flex flex-wrap justify-content-center mt-1">
            {isGallery ? (
              (newValue || []).length > 0 ? (newValue || []).map((img, idx) => (
                <a 
                  key={`new-${key}-${idx}`} 
                  href={img} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="m-1"
                >
                  <img 
                    src={img} 
                    alt={`Updated ${key}-${idx}`} 
                    style={{ 
                      height: '50px', 
                      width: '50px', 
                      objectFit: 'cover',
                      cursor: 'pointer'
                    }} 
                    className="img-thumbnail"
                    title="Click to view full size"
                  />
                </a>
              )) : '-'
            ) : (
              newValue ? (
                <a 
                  href={newValue} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="d-block"
                >
                  <img 
                    src={newValue} 
                    alt={`Updated ${key}`} 
                    style={{ 
                      maxHeight: '70px', 
                      maxWidth: '100%', 
                      objectFit: 'contain',
                      cursor: 'pointer'
                    }} 
                    className="img-thumbnail"
                    title="Click to view full size"
                  />
                </a>
              ) : '-'
            )}
          </div>
        </div>
      </div>
    );
  };

  // Function to render the comparison table for update requests
  const renderComparisonTable = () => {
    // Get only the keys from new_data since these are the ones being updated
    const changedKeys = Object.keys(parsedNewData || {});

    // Create maps for easy lookup
    const originalMap = new Map([
      ...(originalDataProcessed?.fields?.map(f => [f.key, { type: 'field', value: f.value }]) ?? []),
      ...(originalDataProcessed?.images?.map(i => [i.key, { type: 'image', value: i.value }]) ?? []),
      ...(originalDataProcessed?.galleries?.map(g => [g.key, { type: 'gallery', value: g.value }]) ?? []),
    ]);
    const newMap = new Map([
      ...(newDataProcessed?.fields?.map(f => [f.key, { type: 'field', value: f.value }]) ?? []),
      ...(newDataProcessed?.images?.map(i => [i.key, { type: 'image', value: i.value }]) ?? []),
      ...(newDataProcessed?.galleries?.map(g => [g.key, { type: 'gallery', value: g.value }]) ?? []),
    ]);

    return (
      <div style={{ maxHeight: "60vh", overflowY: "auto", overflowX: "auto" }}>
        <Table striped bordered hover responsive size="sm">
          <thead className="table-light">
            <tr>
              <th style={{ width: '25%' }}>Field</th>
              <th style={{ width: '37.5%' }}>Original</th>
              <th style={{ width: '37.5%' }}>Updated</th>
            </tr>
          </thead>
          <tbody>
            {changedKeys.map((key) => {
              const oldEntry = originalMap.get(key);
              const newEntry = newMap.get(key);

              const isMedia = oldEntry?.type === 'image' || oldEntry?.type === 'gallery' || 
                            newEntry?.type === 'image' || newEntry?.type === 'gallery';

              return (
                <tr key={key} className="table-warning">
                  <td>
                    <strong>{formatKey(key)}</strong>
                  </td>
                  {isMedia ? (
                    <td colSpan="2" style={{ padding: '5px' }}>
                      {renderMediaComparison(key, oldEntry, newEntry)}
                    </td>
                  ) : (
                    <>
                      <td style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                        {oldEntry?.value ?? "-"}
                      </td>
                      <td style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                        {newEntry?.value ?? "-"}
                      </td>
                    </>
                  )}
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>
    );
  };


  // Function to render the display for a new item (not an update)
  const renderNewItemDisplay = () => {
    return (
      <div style={{ maxHeight: "60vh", overflowY: "auto", overflowX: "auto" }}>
        {/* Display Regular Fields in a Table */}
        {newDataProcessed.fields.length > 0 && (
          <Table striped bordered hover responsive size="sm" className="mb-3">
            <tbody>
              {newDataProcessed.fields.map(({ key, value }) => (
                <tr key={key}>
                  <td style={{ width: '30%' }}> {/* Set width for key column */}
                    <strong>{formatKey(key)}</strong>
                  </td>
                  <td style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}> {/* Allow wrapping */}
                    {String(value)} {/* Ensure value is displayed as string */}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}

        {/* Display Single Images */}
        {newDataProcessed.images.length > 0 && (
          <div className="mb-3">
            <h6>Images</h6>
            <div className="d-flex flex-wrap align-items-start">
              {newDataProcessed.images.map(({ key, value }) => (
                <div key={key} className="p-2 text-center" style={{ maxWidth: '120px' }}>
                  <small><strong>{formatKey(key)}</strong></small>
                  <a 
                    href={value} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="d-block mt-1"
                  >
                    <img
                      src={value}
                      alt={formatKey(key)}
                      className="img-thumbnail"
                      style={{ 
                        width: "90px", 
                        height: "90px", 
                        objectFit: "cover",
                        cursor: "pointer"
                      }}
                      title="Click to view full size"
                    />
                  </a>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Display Galleries */}
        {newDataProcessed.galleries.length > 0 && (
          <div className="mb-3">
            {newDataProcessed.galleries.map(({ key, value }) => (
              <div key={key} className="mb-3">
                <h6>{formatKey(key)}</h6>
                <div className="d-flex flex-wrap">
                  {value.map((img, index) => (
                    <a 
                      key={`${key}-${index}`} 
                      href={img} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="m-1"
                    >
                      <img
                        src={img}
                        alt={`${formatKey(key)} ${index + 1}`}
                        className="img-thumbnail"
                        style={{ 
                          width: "90px", 
                          height: "90px", 
                          objectFit: "cover",
                          cursor: "pointer"
                        }}
                        title="Click to view full size"
                      />
                    </a>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Message if no data exists at all */}
        {(newDataProcessed.fields.length === 0 && newDataProcessed.images.length === 0 && newDataProcessed.galleries.length === 0) && (
          <p className="text-muted">No details available for this item.</p>
        )}
      </div>
    );
  };


  // --- Render the Modal ---
  return (
    <Modal show={show} onHide={handleClose} centered size="lg" backdrop="static"> {/* Added backdrop static */}
      <Modal.Header closeButton>
        <Modal.Title>
          {isUpdate ? `Review Update: ${resourceType}` : `Review New ${resourceType}`} Approval Request
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isUpdate ? renderComparisonTable() : renderNewItemDisplay()}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Close
        </Button>
        {/* Add Approve/Reject buttons here if needed, passing the approvalRequest.id or similar */}
        {/* Example:
           <Button variant="danger" onClick={() => handleReject(approvalRequest.id)}>Reject</Button>
           <Button variant="success" onClick={() => handleApprove(approvalRequest.id)}>Approve</Button>
        */}
      </Modal.Footer>
    </Modal>
  );
};

export default ViewApprovalRequestModal;